import React, { useState, useEffect } from "react";
import { Icon } from "../components/ui";

const ROLE_LABEL = { SUPER_ADMIN: "Super Admin", ADMIN: "Admin", SUPERVISOR: "Supervisor", USER: "Production", CLIENT_ADMIN: "Client Admin", AUDITOR: "Auditor" };
const ROLE_SUB = {
  SUPER_ADMIN: "Manage console accounts",
  ADMIN: "Import, review and assign records",
  SUPERVISOR: "Oversee and support the team",
  USER: "Work on records assigned to you (production)",
  CLIENT_ADMIN: "Client-side administrator",
  AUDITOR: "Review records for quality",
};

/**
 * Shown after login when there's more than one choice to make.
 * Flow: pick role -> pick client (if the account has >1).
 * Process is no longer chosen here — a production user sees all their processes as tabs in "My records".
 * Calls onPick(role, null, client|null).
 */
export function RolePicker({ user, onPick }) {
  const roles = user.roles && user.roles.length ? user.roles : [user.role];
  const clients = user.groups || [];

  const [role, setRole] = useState(roles.length === 1 ? roles[0] : null);
  const [client, setClient] = useState(clients.length === 1 ? clients[0] : null);

  // Admin / Super admin are not group-based — never ask them to pick a client.
  const groupBased = role === "USER" || role === "SUPERVISOR";
  const effClient = groupBased ? client : null;
  const clientChosen = !groupBased || clients.length === 0 || client !== null;

  // auto-finish when nothing (more) is left to choose
  useEffect(() => {
    if (!role) return;
    if (!clientChosen) return;
    onPick(role, null, effClient);
  }, [role, client]); // eslint-disable-line

  const pickingClient = groupBased && clients.length > 1 && client === null;

  const step = !role ? "role" : pickingClient ? "client" : "done";
  const stepNum = step === "role" ? 1 : 2;
  const totalSteps = 1 + (clients.length > 1 ? 1 : 0);
  const stepTitle = step === "role" ? "Choose your role" : "Choose a client";
  const stepHint = step === "role" ? "You have access to more than one role. Pick how you'd like to sign in."
    : "Select the client group you're working for today.";

  return (
    <div className="picker-screen" style={{ backgroundImage: "url(/images/login.png)" }}>
      <div className="picker-card">
        <aside className="picker-side">
          <div className="picker-brand"><span className="picker-brand-rx">Rx</span> PriorAuth</div>
          <div className="picker-welcome">
            <div className="picker-hi">Welcome,</div>
            <div className="picker-name">{user.firstName}</div>
            <p className="picker-side-sub">Let's get you into the right workspace.</p>
          </div>
          {totalSteps > 1 && (
            <div className="picker-dots">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <span key={i} className={"picker-dot" + (i + 1 === stepNum ? " on" : "") + (i + 1 < stepNum ? " done" : "")} />
              ))}
              <span className="picker-dots-label">Step {stepNum} of {totalSteps}</span>
            </div>
          )}
        </aside>

        <main className="picker-main">
          <div className="picker-main-head">
            <h2 className="picker-title">{stepTitle}</h2>
            <p className="picker-sub">{stepHint}</p>
          </div>

          {!role && (
            <div className="picker-grid">
              {roles.map((r) => (
                <button key={r} className="picker-tile" onClick={() => setRole(r)}>
                  <span className="picker-ico"><Icon.users width="20" height="20" /></span>
                  <span className="picker-txt"><b>{ROLE_LABEL[r] || r}</b><span>{ROLE_SUB[r] || ""}</span></span>
                  <span className="picker-arrow"><Icon.chevron width="18" height="18" /></span>
                </button>
              ))}
            </div>
          )}

          {pickingClient && (
            <>
              <div className="picker-grid">
                {clients.map((c) => (
                  <button key={c} className="picker-tile" onClick={() => setClient(c)}>
                    <span className="picker-ico"><Icon.clipboard width="20" height="20" /></span>
                    <span className="picker-txt"><b>{c}</b><span>Client group</span></span>
                    <span className="picker-arrow"><Icon.chevron width="18" height="18" /></span>
                  </button>
                ))}
              </div>
              {roles.length > 1 && <button className="picker-back" onClick={() => setRole(null)}>← Back to roles</button>}
            </>
          )}
        </main>
      </div>
    </div>
  );
}